import type { MonthlyPoint } from '../../../hooks/useReports';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

interface Props {
  monthlyData: MonthlyPoint[];
}

const fmt = (v: number) =>
  v >= 1000 ? `R$ ${(v / 1000).toFixed(1)}k` : `R$ ${v.toFixed(0)}`;

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-3 text-sm">
      <p className="font-600 text-gray-700 mb-2">{label}</p>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-xs mb-1">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-gray-500">{p.dataKey === 'receitas' ? 'Receitas' : 'Despesas'}:</span>
          <span className="font-600">
            {Number(p.value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </span>
        </div>
      ))}
    </div>
  );
};

export const IncomeExpenseChart = ({ monthlyData }: Props) => {
  const hasData = monthlyData.some(d => d.receitas > 0 || d.despesas > 0);

  if (!hasData) {
    return (
      <div className="h-[280px] flex flex-col items-center justify-center gap-2 text-center">
        <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-2xl mb-1">
          <i className="ri-bar-chart-2-line text-2xl text-gray-300" />
        </div>
        <p className="text-sm font-600 text-gray-400">Sem dados no período</p>
        <p className="text-xs text-gray-300">Registre lançamentos para ver o gráfico</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={monthlyData} barGap={3} barCategoryGap="35%">
        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
        <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} />
        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 11 }} tickFormatter={fmt} width={58} />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F9FAFB' }} />
        <Legend iconType="circle" iconSize={8} formatter={(v) => v === 'receitas' ? 'Receitas' : 'Despesas'} />
        <Bar dataKey="receitas" fill="#10B981" radius={[6, 6, 0, 0]} maxBarSize={38} />
        <Bar dataKey="despesas" fill="#FCA5A5" radius={[6, 6, 0, 0]} maxBarSize={38} />
      </BarChart>
    </ResponsiveContainer>
  );
};
